import { useEffect, useRef, useState } from 'preact/hooks'
import { extractPagesAsPdf } from '../lib/pdf'

type Props = {
  file: File
  pages: number[]
  /** Stable key per invoice — used for caching extracted blob URLs. */
  cacheKey: string
}

/**
 * Right-pane PDF preview. Extracts the requested pages from `file` with
 * pdf-lib, makes an in-browser blob URL, and renders it in a native PDF
 * iframe (matching the paid product's PdfSplitEditorModal). Blob URLs are
 * cached per-invoice id so re-clicks are instant, and revoked when the
 * component unmounts.
 */
export default function PdfPreviewPane({ file, pages, cacheKey }: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const cache = useRef<Map<string, string>>(new Map())
  const aborted = useRef(false)

  useEffect(() => {
    aborted.current = false

    const cached = cache.current.get(cacheKey)
    if (cached) {
      setUrl(cached)
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    extractPagesAsPdf(file, pages)
      .then((blob) => {
        if (aborted.current) return
        const objectUrl = URL.createObjectURL(blob)
        cache.current.set(cacheKey, objectUrl)
        setUrl(objectUrl)
      })
      .catch((e) => {
        if (aborted.current) return
        const msg = e instanceof Error ? e.message : 'Could not load preview.'
        setError(msg)
      })
      .finally(() => {
        if (!aborted.current) setLoading(false)
      })

    return () => {
      aborted.current = true
    }
  }, [file, cacheKey, pages])

  // Revoke every cached blob URL when the component unmounts (e.g. modal closes).
  useEffect(() => {
    const cacheRef = cache.current
    return () => {
      for (const objectUrl of cacheRef.values()) {
        URL.revokeObjectURL(objectUrl)
      }
      cacheRef.clear()
    }
  }, [])

  return (
    <div class="sis-preview">
      {loading && (
        <div class="sis-preview-loading" aria-busy="true">
          <div class="sis-preview-shimmer" />
          <span>Loading preview…</span>
        </div>
      )}
      {error && !loading && (
        <div class="sis-preview-error">{error}</div>
      )}
      {url && !loading && !error && (
        <iframe
          key={cacheKey}
          src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          class="sis-preview-iframe"
          title="Invoice preview"
        />
      )}
    </div>
  )
}
